import { useState, useEffect } from "react";
import "./candidatePostLoginValueAdd.css";
import { CareerGuidanceService } from "../../../services/careerGuidanceService";
import { serverConfig } from "../../../services/server-config";
import { CandidatePostLoginService } from "../../../services/candidatePostLoginService";
import { CandidateService, decodeJWT } from "../../../services/candidateService";

const CareerGuidanceImage = ({ imageUrl, alt }) => {
    const [imageSrc, setImageSrc] = useState(null);
    const [imageLoading, setImageLoading] = useState(true);

    useEffect(() => {
        let objectUrl = null;
        let cancelled = false;

        const fetchImage = async () => {
            if (!imageUrl) {
                setImageLoading(false);
                return;
            }
            if (imageUrl.startsWith("http")) {
                setImageSrc(imageUrl);
                setImageLoading(false);
                return;
            }
            try {
                setImageLoading(true);
                const url =
                    `https://${serverConfig.serverBaseAddress}/signite/file/download?path=${encodeURIComponent(imageUrl)}`;
                const response = await fetch(url, {
                    headers: CandidateService.getAuthHeader(),
                });

                if (!response.ok) {
                    throw new Error("Failed to load image");
                }

                const blob = await response.blob();
                if (cancelled) return;
                objectUrl = URL.createObjectURL(blob);
                setImageSrc(objectUrl);
            } catch (error) {
                console.error(
                    "Career Guidance image loading failed:",
                    error
                );
            } finally {
                if (!cancelled) {
                    setImageLoading(false);
                }
            }
        };

        fetchImage();

        return () => {
            cancelled = true;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [imageUrl]);

    return (
        <div className="career-guidance-image-wrapper">
            {imageLoading && (
                <div className="career-guidance-image-loader">
                    Loading...
                </div>
            )}
            {imageSrc && (
                <img
                    src={imageSrc}
                    alt={alt || "Career Guidance"}
                    className="card-img-top career-guidance-image"
                    decoding="async"
                />
            )}

        </div>
    );
};


const CandidatePostLoginValueAdd = () => {
    const [loader, setLoader] = useState(false);
    const [sessions, setSessions] = useState([]);
    const [sessionPageNo, setSessionPageNo] = useState(0);
    const sessionPageSize = 8;
    const [sessionPageCount, setSessionPageCount] = useState(1);
    const [sessionTotalCount, setSessionTotalCount] = useState(0);
    const [sessionHasNext, setSessionHasNext] = useState(false);
    const [showSlotModal, setShowSlotModal] = useState(false);
    const [slots, setSlots] = useState([]);
    const [selectedSession, setSelectedSession] = useState(null);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [loadingSessionId, setLoadingSessionId] = useState(null);
    const [activeTab, setActiveTab] = useState("sessions");
    const [bookings, setBookings] = useState([]);
    const [bookingPageNo, setBookingPageNo] = useState(0);
    const bookingPageSize = 5;
    const [bookingPageCount, setBookingPageCount] = useState(1);
    const [bookingTotalCount, setBookingTotalCount] = useState(0);
    const [bookingHasNext, setBookingHasNext] = useState(false);
    const [paginationLoading, setPaginationLoading] = useState(false);
    const [showPaymentSuccessModal, setShowPaymentSuccessModal] = useState(false);
    const [candidateProfile, setCandidateProfile] = useState(null);
    const [directBookingPending, setDirectBookingPending] = useState(false);

    const token = window.localStorage.getItem("authToken");
    const decoded = decodeJWT(token);
    const candidateId = decoded?.username || "";

    useEffect(() => {
        if (!candidateId) return;
        const fetchCandidate = async () => {
            try {
                const response = await CandidateService.getCandidateById(candidateId);
                if (
                    response.status === 200 &&
                    response.data?.candidates?.length > 0
                ) {
                    const candidate = response.data.candidates[0].candidate;
                    setCandidateProfile(candidate);
                }
            } catch (error) {
                console.error(
                    "Failed to fetch candidate profile:",
                    error
                );
            }
        };
        fetchCandidate();
    }, [candidateId]);

    const changeTab = async (tab) => {
        if (tab === activeTab) {
            return;
        }
        setLoader(true);
        try {
            if (tab === "bookings") {
                await loadBookings();
            }
            if (tab === "sessions") {
                await loadSessions();
            }
            setActiveTab(tab);
        } finally {
            setLoader(false);
        }
    };

    const loadSessions = async (page = sessionPageNo, showLoader = true) => {
        try {
            if (showLoader) {
                setLoader(true);
            }
            const response =
                await CareerGuidanceService.getSessions(
                    page,
                    sessionPageSize
                );
            const data = response.data;
            setSessions(
                Array.isArray(data?.sessions)
                    ? data.sessions
                    : []
            );
            setSessionPageNo(page);
            setSessionPageCount(data?.pageCount || 1);
            setSessionTotalCount(data?.totalCount || 0);
            setSessionHasNext(data?.hasNext || false);
        } catch (error) {
            console.error("Error loading sessions:", error);
            setSessions([]);
            setSessionTotalCount(0);
            setSessionPageCount(1);
            setSessionHasNext(false);
        } finally {
            if (showLoader) {
                setLoader(false);
            }
        }
    };

    useEffect(() => {
        loadSessions();
    }, []);

    const loadBookings = async (page = bookingPageNo) => {
        try {
            const response =
                await CareerGuidanceService.getBookings(
                    page,
                    bookingPageSize
                );
            const data = response.data;
            setBookings(
                Array.isArray(data?.bookings)
                    ? data.bookings
                    : []
            );
            setBookingPageNo(page);
            setBookingPageCount(
                data?.pageCount || 1
            );
            setBookingTotalCount(
                data?.totalCount || 0
            );
            setBookingHasNext(
                data?.hasNext || false
            );
        } catch (error) {
            console.error("Error loading bookings:", error);
            setBookings([]);
            setBookingTotalCount(0);
            setBookingPageCount(1);
            setBookingHasNext(false);

        }
    };

    const handlePagination = async (page, onPageChange) => {
        setPaginationLoading(true);
        try {
            await onPageChange(page, false);
        } finally {
            setPaginationLoading(false);
        }
    };

    const renderPagination = (
        pageNo,
        pageCount,
        hasNext,
        onPageChange
    ) => {
        if (pageCount <= 1) {
            return null;
        }
        const currentPage = pageNo + 1;
        const pageNumbers = [];
        if (pageCount <= 5) {
            for (let i = 1; i <= pageCount; i++) {
                pageNumbers.push(i);
            }
        } else {
            pageNumbers.push(1);
            if (currentPage > 3) {
                pageNumbers.push("...");
            }
            const start = Math.max(2, currentPage - 1);
            const end = Math.min(
                pageCount - 1,
                currentPage + 1
            );
            for (let i = start; i <= end; i++) {
                pageNumbers.push(i);
            }
            if (currentPage < pageCount - 2) {
                pageNumbers.push("...");
            }
            pageNumbers.push(pageCount);
        }

        return (
            <div className="pagination-container">
                <div className="pagination-controls d-flex justify-content-center align-items-center gap-1">
                    <button
                        className="border rounded p-1"
                        onClick={() =>
                            handlePagination(0, onPageChange)
                        }
                        disabled={
                            pageNo === 0 || paginationLoading
                        }
                    >
                        {'<<'}
                    </button>
                    <button
                        className="border rounded p-1"
                        onClick={() =>
                            handlePagination(
                                Math.max(0, pageNo - 1),
                                onPageChange
                            )
                        }
                        disabled={
                            pageNo === 0 || paginationLoading
                        }
                    >
                        {'<'}
                    </button>
                    {pageNumbers.map((page, index) => (

                        <button
                            key={index}
                            className={`border rounded p-1 ${currentPage === page
                                ? "active"
                                : ""
                                }`}
                            disabled={
                                typeof page !== "number" ||
                                paginationLoading
                            }
                            onClick={() => {
                                if (
                                    typeof page === "number"
                                ) {
                                    handlePagination(
                                        page - 1,
                                        onPageChange
                                    );
                                }
                            }}
                        >
                            {page}
                        </button>
                    ))}
                    <button
                        className="border rounded p-1"
                        onClick={() =>
                            handlePagination(
                                pageNo + 1,
                                onPageChange
                            )
                        }
                        disabled={
                            !hasNext || paginationLoading
                        }
                    >
                        {'>'}
                    </button>
                    <button
                        className="border rounded p-1"
                        onClick={() =>
                            handlePagination(
                                pageCount - 1,
                                onPageChange
                            )
                        }
                        disabled={
                            !hasNext || paginationLoading
                        }
                    >
                        {'>>'}
                    </button>
                </div>
            </div>
        );
    };

    const proceedToPayment = async () => {
        if (!selectedSession) {
            alert("Session details not found.");
            return;
        }

        if (
            selectedSession.bookingType === "SLOT_BASED" &&
            !selectedSlot
        ) {
            alert("Please select a slot.");
            return;
        }
        try {
            const request = {
                sessionId: selectedSession.id,
                gateway: "RAZORPAY",
                ...(selectedSession.bookingType === "SLOT_BASED" && {
                    slotId: selectedSlot.id,
                }),
            };
            const response = await CareerGuidanceService.purchaseSession(request);
            if (
                response.status !== 200 &&
                response.status !== 201 &&
                response.status !== 202
            ) {
                alert("Unable to initiate payment.");
                return;
            }
            const order = response?.data?.paymentGatewayDTO?.razorpayOrder;
            const transactionId = response?.data?.transactionId;
            if (!order?.orderId) {
                alert("Unable to create payment order.");
                return;
            }
            const options = {
                key: serverConfig.razorPayKey,
                amount: order.amount,
                currency: order.currency,
                name: "Leaderly",
                description: selectedSession.headline || "Career Guidance Session",
                order_id: order.orderId,
                prefill: {
                    name: candidateProfile?.personalDetails?.firstName
                        ? `${candidateProfile.personalDetails.firstName} ${candidateProfile.personalDetails.lastName || ""
                            }`.trim()
                        : "",
                    email: candidateProfile?.personalDetails?.primaryEmailId || "",
                    contact: candidateProfile?.personalDetails?.primaryPhoneNumber || "",
                },
                notes: {
                    sessionId: selectedSession.id,

                    ...(selectedSession.bookingType === "SLOT_BASED" && {
                        slotId: selectedSlot.id,
                    }),

                    ...(transactionId && {
                        transactionId: transactionId,
                    }),
                },
                theme: {
                    color: "#F35824",
                },

                handler: async function (paymentResponse) {
                    try {
                        const verifyPayload = {
                            razorpay_payment_id:
                                paymentResponse.razorpay_payment_id,
                            razorpay_order_id:
                                paymentResponse.razorpay_order_id,
                            razorpay_signature:
                                paymentResponse.razorpay_signature,
                        };

                        const verifyResponse =
                            await CandidatePostLoginService.verifyPayment(
                                verifyPayload
                            );

                        if (
                            verifyResponse.status === 200 ||
                            verifyResponse.status === 201 ||
                            verifyResponse.status === 202
                        ) {
                            setShowPaymentSuccessModal(true);
                            setShowSlotModal(false);
                            setSelectedSlot(null);
                            setSelectedSession(null);
                            await loadSessions();
                            await loadBookings();
                        } else {
                            alert(
                                "Payment was successful, but verification is still pending."
                            );
                        }
                    } catch (err) {
                        console.error(
                            "Payment verification failed:",
                            err
                        );

                        alert(
                            "Payment was successful. Booking confirmation is being processed."
                        );
                        setShowSlotModal(false);
                        setTimeout(async () => {
                            try {
                                await loadSessions();
                                await loadBookings();
                            } catch (refreshError) {
                                console.error(
                                    "Failed to refresh booking data:",
                                    refreshError
                                );
                            }
                        }, 2000);
                    }
                },
                modal: {
                    ondismiss: function () {
                        console.log(
                            "Razorpay payment window closed."
                        );

                    },
                },
            };

            const rzp = new window.Razorpay(options);
            rzp.on("payment.failed", function (response) {
                console.error(
                    "Razorpay Payment Failed:",
                    response
                );
                alert(
                    response?.error?.description ||
                    "Payment failed. Please try again."
                );
            });
            rzp.open();
        } catch (error) {
            console.error(
                "Unable to purchase session:",
                error
            );
            alert(
                error?.response?.data?.message ||
                "Unable to purchase session."
            );
        }
    };

    const handleBuy = async (session) => {
        setSelectedSession(session);
        setSelectedSlot(null);

        if (session.bookingType === "DIRECT_BOOKING") {
            setDirectBookingPending(true);
            return;
        }

        try {
            setLoadingSessionId(session.id);
            const response = await CareerGuidanceService.getSlots(session.id);
            const data = response.data;

            setSlots(
                Array.isArray(data?.slots)
                    ? data.slots
                    : []
            );
            setShowSlotModal(true);
        } catch (error) {
            console.error(error);
            alert("Unable to load slots.");
            setSlots([]);
        } finally {
            setLoadingSessionId(null);
        }
    };

    useEffect(() => {
        if (directBookingPending && selectedSession) {
            setDirectBookingPending(false);
            proceedToPayment();
        }
    }, [directBookingPending, selectedSession]);

    const formatDateTime = (timestamp) => {
        if (!timestamp) return "";
        return new Date(timestamp).toLocaleString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });

    };

    return (
        <>
            {(loader || paginationLoading) && (
                <div className="candidate-loader-container">
                    <div className="candidate-loader"></div>
                </div>
            )}
            <div>
                <div className="container my-5">
                    <div className="value-add-courses-section mt-5">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h4 className="courses-heading">Career Guidance</h4>
                        </div>
                        <div className="career-tabs mb-4">
                            <button
                                className={`career-tab ${activeTab === "sessions" ? "active" : ""}`}
                                onClick={() => changeTab("sessions")}
                            >
                                Sessions
                            </button>

                            <button
                                className={`career-tab ${activeTab === "bookings" ? "active" : ""}`}
                                onClick={() => changeTab("bookings")}
                            >
                                My Bookings
                            </button>
                        </div>
                        {activeTab === "sessions" && (
                            <>
                                <div className="row gy-4 sessions-list">
                                    {sessions.map((session) => (
                                        <div key={session.id} className="col-12 col-md-6 col-lg-3">
                                            <div className="card h-100 course-card">
                                                {session.imageUrl ? (
                                                    <CareerGuidanceImage
                                                        imageUrl={session.imageUrl}
                                                        alt={session.headline}
                                                    />
                                                ) : null}

                                                <div className="card-body d-flex flex-column">
                                                    <h6 className="course-title">
                                                        {session.headline}
                                                    </h6>
                                                    <p className="course-description">
                                                        {session.descriptionText}
                                                    </p>
                                                    <div className="d-flex justify-content-between align-items-center mt-auto">
                                                        <span className="course-price">
                                                            ₹ {session.price}
                                                        </span>

                                                        <button
                                                            className="btn course-buy-btn"
                                                            onClick={() => handleBuy(session)}
                                                            disabled={loadingSessionId !== null}
                                                        >
                                                            {loadingSessionId === session.id
                                                                ? "Loading..."
                                                                : session.bookingType === "SLOT_BASED"
                                                                    ? "Book a Slot"
                                                                    : "Book"}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {renderPagination(
                                    sessionPageNo,
                                    sessionPageCount,
                                    sessionHasNext,
                                    loadSessions
                                )}
                            </>
                        )}
                        {activeTab === "bookings" && (
                            <>
                                {bookings.length === 0 ? (
                                    <div className="text-center py-5">
                                        No Bookings Found
                                    </div>
                                ) : (
                                    <>
                                        <div className="total-batches-count">
                                            Total Bookings : {bookingTotalCount}
                                        </div>

                                        <div className="booking-table-wrapper bookings-list">
                                            <table className="booking-table">
                                                <thead>
                                                    <tr>
                                                        <th>Session</th>
                                                        <th>Session Start Time</th>
                                                        <th>Session End Time</th>
                                                        <th>Amount</th>
                                                        <th>Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {bookings.map((booking) => (
                                                        <tr key={booking.id}>
                                                            <td>
                                                                <strong>
                                                                    {booking.sessionHeadline}
                                                                </strong>
                                                            </td>

                                                            <td>
                                                                {booking.startTime
                                                                    ? formatDateTime(booking.startTime)
                                                                    : "Not Scheduled"}
                                                            </td>

                                                            <td>
                                                                {booking.endTime
                                                                    ? formatDateTime(booking.endTime)
                                                                    : "Not Scheduled"}
                                                            </td>

                                                            <td>
                                                                {booking.currency}{" "}
                                                                {booking.price}
                                                            </td>

                                                            <td>
                                                                <span
                                                                    className={`booking-status ${booking.status.toLowerCase()}`}
                                                                >
                                                                    {booking.status}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        {renderPagination(
                                            bookingPageNo,
                                            bookingPageCount,
                                            bookingHasNext,
                                            loadBookings
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>
                {showSlotModal && (
                    <div
                        className="modal d-block candidate-modal"
                        style={{
                            backgroundColor: "rgba(0,0,0,0.5)"
                        }}
                    >
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <div>
                                        <h5 className="modal-title">
                                            Select Slot
                                        </h5>
                                    </div>

                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => {
                                            setShowSlotModal(false);
                                            setSelectedSlot(null);
                                            setSelectedSession(null);
                                        }}
                                    />
                                </div>
                                <div className="modal-body">
                                    {selectedSession && (
                                        <div className="mb-3">
                                            <strong>
                                                {selectedSession.headline}
                                            </strong>
                                            <div className="text-muted">
                                                ₹ {selectedSession.price}
                                            </div>
                                        </div>
                                    )}
                                    {slots.length === 0 ? (
                                        <p>
                                            No slots available.
                                        </p>
                                    ) : (
                                        slots.map((slot) => (
                                            <div
                                                key={slot.id}
                                                className={`border rounded p-3 mb-3 ${selectedSlot?.id === slot.id
                                                    ? "border-primary"
                                                    : ""
                                                    }`}
                                            >

                                                <div className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        id={`slot-${slot.id}`}
                                                        name="slot"
                                                        checked={
                                                            selectedSlot?.id === slot.id
                                                        }
                                                        onChange={() =>
                                                            setSelectedSlot(slot)
                                                        }
                                                    />
                                                    <label
                                                        htmlFor={`slot-${slot.id}`}
                                                        className="form-check-label ms-2 w-100"
                                                    >
                                                        <h6 className="mb-2">
                                                            {formatDateTime(
                                                                slot.startTime
                                                            )}
                                                        </h6>
                                                        <div className="text-muted">
                                                            Ends:{" "}
                                                            {formatDateTime(
                                                                slot.endTime
                                                            )}
                                                        </div>
                                                        <div className="text-success mt-2">
                                                            Available Seats:{" "}
                                                            {slot.availableSeats}
                                                        </div>
                                                    </label>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-cancel"
                                        onClick={() => {
                                            setShowSlotModal(false);
                                            setSelectedSlot(null);
                                            setSelectedSession(null);
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-ok"
                                        onClick={proceedToPayment}
                                        disabled={!selectedSlot}
                                    >
                                        Proceed to Payment
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {showPaymentSuccessModal && (
                <div
                    className="modal d-block candidate-modal"
                    style={{
                        backgroundColor: "rgba(0,0,0,0.5)"
                    }}
                >

                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    Payment Successful
                                </h5>

                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() =>
                                        setShowPaymentSuccessModal(false)
                                    }
                                />
                            </div>
                            <div className="modal-body">
                                <p className="mb-0">
                                    Your session booking has been confirmed.
                                    Please check your email for the confirmation.
                                </p>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-ok"
                                    onClick={() =>
                                        setShowPaymentSuccessModal(false)
                                    }
                                >
                                    OK
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CandidatePostLoginValueAdd;